import type { Appointment, AppointmentParticipant } from '@medplum/fhirtypes';
import format from 'date-fns/format';
import isValid from 'date-fns/isValid';
import parseISO from 'date-fns/parseISO';

/**
 * Participants are BackboneElements (not top-level Reference fields), so the
 * walker's slot discovery does not see them — views read them directly by
 * the actor's resource type.
 */
export function participantOfType(
  data: Appointment,
  resourceType: 'Patient' | 'Practitioner' | 'Location',
): AppointmentParticipant | undefined {
  return data.participant?.find((participant) =>
    participant.actor?.reference?.startsWith(`${resourceType}/`),
  );
}

export function participantDisplay(
  data: Appointment,
  resourceType: 'Patient' | 'Practitioner' | 'Location',
): string | undefined {
  return participantOfType(data, resourceType)?.actor?.display;
}

function parse(value: string | undefined): Date | undefined {
  if (!value) return undefined;
  const parsed = parseISO(value);
  return isValid(parsed) ? parsed : undefined;
}

/** "Wednesday, Jul 15" — local time; appointments are timed events. */
export function appointmentDayText(data: Appointment): string | undefined {
  const start = parse(data.start);
  return start ? format(start, 'EEEE, MMM d') : undefined;
}

/** "10:00 AM – 11:00 AM" (or just the start when there is no end). */
export function appointmentTimeRangeText(
  data: Appointment,
): string | undefined {
  const start = parse(data.start);
  if (!start) return undefined;
  const end = parse(data.end);
  const startText = format(start, 'h:mm a');
  return end ? `${startText} – ${format(end, 'h:mm a')}` : startText;
}
